import { clockLabel, durationLabel, localTimeCandidates } from "./consolidationPresentation.js";
import { serviceInstant } from "./consolidationAutofill.js";
import { isLunchBreakSegment } from "./lunchBreakTimeline.js";

// Seuils des avertissements de saisie manuelle; ils signalent sans bloquer la validation.
export const TIME_CHECKS = { minServiceMinutes: 2 * 60, maxServiceMinutes: 16 * 60, maxLunchMinutes: 90 };

function instantOf(value, timezone) {
  if (!value) return NaN;
  if (/Z$|[+-]\d{2}:\d{2}$/.test(value)) return Date.parse(value);
  const candidates = localTimeCandidates(value, timezone);
  return candidates.length === 1 ? Date.parse(candidates[0].instant) : NaN;
}

const minutesBetween = (start, end, timezone) => (instantOf(end, timezone) - instantOf(start, timezone)) / 60000;

// Dîner posé au Dispatch (Paramètres système) : hors temps travaillé, comme pour les heures max.
export const plannedLunchMinutes = row => (row.dispatch_assignment?.trips || [])
  .flatMap(trip => trip?.route?.segment_distances || []).filter(isLunchBreakSegment)
  .reduce((sum, segment) => sum + Number(segment.manual_buffer_minutes ?? segment.manualBufferMinutes), 0);

// Durée du service prévue au Dispatch, moins le dîner prévu.
export function plannedServiceMinutes(row) {
  const start = serviceInstant(row.planned_service_start, row);
  return minutesBetween(start, serviceInstant(row.planned_service_end, row, start), row.timezone) - plannedLunchMinutes(row);
}

// Avertissements sur les durées du service et du dîner, indépendants des horaires prévus du Dispatch.
export function timeWarnings(row) {
  const warnings = {};
  const add = (key, message) => { (warnings[key] ||= []).push(message); };
  const service = minutesBetween(row.service_start, row.service_end, row.timezone);
  if (service > TIME_CHECKS.maxServiceMinutes || (service > 0 && service < TIME_CHECKS.minServiceMinutes)) {
    add("service_end", `Service de ${durationLabel(service)} : vérifiez le début et la fin.`);
  }
  const lunch = row.no_lunch ? NaN : minutesBetween(row.lunch_start, row.lunch_end, row.timezone);
  if (lunch > TIME_CHECKS.maxLunchMinutes) add("lunch_end", `Dîner de ${durationLabel(lunch)} : vérifiez les heures.`);
  return warnings;
}

export function quantityDiffers(actual, planned) {
  return actual != null && actual !== "" && planned != null && planned !== ""
    && Number.isFinite(Number(actual)) && Number.isFinite(Number(planned))
    && Number(actual) !== Number(planned);
}

export function isEarlier(value, previous, timezone) {
  return instantOf(value, timezone) < instantOf(previous, timezone);
}

// Incohérences qui interdisent d'enregistrer : un départ avant celui de l'arrêt précédent (ordre réel) ou avant sa propre
// arrivée, et une arrivée avant le départ de l'arrêt précédent (deux arrêts qui se chevauchent, refusés par le calcul des heures).
export function chronologyErrors(row) {
  const visits = row.visits.filter(visit => visit.pm?.role !== "target").sort((a, b) => a.actual_sequence - b.actual_sequence);
  const earlier = earlierVisitTimes(visits, row.timezone);
  const clock = value => clockLabel(value, row.timezone, row.dispatch_date);
  let previousDeparture = null;
  return visits.flatMap((visit, index) => {
    const errors = [
      ...(earlier[index].departure ? [`Le départ de ${visit.label} (${clock(visit.departure)}) précède celui de l’arrêt précédent.`] : []),
      ...(isEarlier(visit.departure, visit.arrival, row.timezone)
        ? [`Le départ de ${visit.label} (${clock(visit.departure)}) précède son arrivée (${clock(visit.arrival)}).`] : []),
      ...(isEarlier(visit.arrival, previousDeparture, row.timezone)
        ? [`L’arrivée à ${visit.label} (${clock(visit.arrival)}) précède le départ de l’arrêt précédent (${clock(previousDeparture)}).`] : []),
    ];
    if (visit.departure) previousDeparture = visit.departure;
    return errors;
  });
}

export function fullTimeLabel(value, timezone) {
  if (!value) return "—";
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(value)) return value.replace("T", " · ");
  return new Intl.DateTimeFormat("fr-CA", { timeZone: timezone, dateStyle: "short", timeStyle: "long" }).format(new Date(value));
}

export function earlierVisitTimes(visits, timezone) {
  const previous = {};
  return visits.map((visit) => {
    const earlier = {};
    for (const field of ["arrival", "departure"]) {
      earlier[field] = isEarlier(visit[field], previous[field], timezone);
      if (visit[field]) previous[field] = visit[field];
    }
    return earlier;
  });
}
